import React from 'react';
import './About.css';
import { FaLayerGroup, FaBolt, FaDatabase, FaShieldAlt, FaCogs } from 'react-icons/fa';
import { metricsData } from '../data/metricsData';

const architecturalTenets = [
    {
        icon: <FaBolt className="tenet-icon" />,
        title: 'Concurrency & Event Streaming',
        desc: 'Clustered Node.js event-loops and WebSockets synchronization for high-throughput live client streams.',
    },
    {
        icon: <FaDatabase className="tenet-icon" />,
        title: 'High-Throughput Data Tier',
        desc: 'Sub-millisecond Redis cache layers, MongoDB compound indexing, and ACID PostgreSQL/MySQL schemas.',
    },
    {
        icon: <FaShieldAlt className="tenet-icon" />,
        title: 'Zero-Trust Security & HMAC',
        desc: 'HMAC-SHA256 webhook signatures, stateless JWT rotation, and defense-in-depth API rate limiters.',
    },
    {
        icon: <FaCogs className="tenet-icon" />,
        title: 'Production Resilience & CI/CD',
        desc: 'Zero-downtime rolling deployments, PM2 process clustering, and automated Jest/GitHub Actions pipelines.',
    },
];

const About = () => {
    return (
        <section className="about" id="about">
            <div className="cyber-grid"></div>

            <div className="section-container">
                {/* Section Header */}
                <div className="section-header">
                    <span className="section-badge">ABOUT &amp; IMPACT</span>
                    <h2 className="section-title">Engineered for Scale &amp; Performance</h2>
                    <p className="section-subtitle">
                        Core architectural principles, quantified production benchmarks, and systems engineering tenets powering high-concurrency cloud platforms.
                    </p>
                </div>

                {/* Unified Telemetry Ribbon */}
                <div className="about-telemetry-ribbon" role="region" aria-label="Quantified Impact Metrics">
                    {metricsData.map((m) => (
                        <div className="telemetry-compartment" key={m.id}>
                            <div className="metric-val-wrap">
                                <span className="about-metric-val">{m.value}</span>
                            </div>
                            <div className="about-metric-label">{m.label}</div>
                            <div className="about-metric-sub">{m.sub}</div>
                        </div>
                    ))}
                </div>

                {/* 2-Column Split: Narrative & Pillars */}
                <div className="about-split-layout">
                    {/* Left: Executive Narrative & 10-Second Recruiter Intake */}
                    <div className="about-profile-panel">
                        <div className="panel-kicker">EXECUTIVE PROFILE</div>
                        <h3 className="profile-heading">Distributed Systems Engineer Delivering Resilient Cloud Architecture</h3>
                        <p className="bio-lead">
                            Architecting high-concurrency Node.js backends, sub-50ms real-time event loops, multi-database topologies, and end-to-end production platforms.
                        </p>

                        <div className="executive-key-points">
                            <div className="key-point-row">
                                <div className="key-point-icon"><FaBolt /></div>
                                <div className="key-point-text">
                                    <strong>Sub-50ms Concurrency:</strong> Clustered Node.js worker scaling, non-blocking asynchronous event loops, and real-time Kafka &amp; WebSockets messaging.
                                </div>
                            </div>
                            <div className="key-point-row">
                                <div className="key-point-icon"><FaDatabase /></div>
                                <div className="key-point-text">
                                    <strong>Polyglot Persistence:</strong> High-throughput MongoDB Atlas compound indexing, ACID PostgreSQL/MySQL transactions, and sub-millisecond Redis distributed caching.
                                </div>
                            </div>
                            <div className="key-point-row">
                                <div className="key-point-icon"><FaShieldAlt /></div>
                                <div className="key-point-text">
                                    <strong>Zero-Downtime Reliability:</strong> Automated Nginx reverse-proxy load balancing, PM2 process clustering, and cryptographically signed HMAC-SHA256 gateways.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Architectural Core Tenets */}
                    <div className="about-tenets-panel">
                        <div className="arch-tenets-header">
                            <FaLayerGroup className="tenets-header-icon" />
                            <span>Core Architectural Pillars</span>
                        </div>
                        <div className="arch-tenets-grid">
                            {architecturalTenets.map((tenet, idx) => (
                                <div className="tenet-card" key={idx}>
                                    <div className="tenet-card-top">
                                        <div className="tenet-icon-wrap">
                                            {tenet.icon}
                                        </div>
                                        <span className="tenet-number">0{idx + 1}</span>
                                    </div>
                                    <div className="tenet-content">
                                        <h4 className="tenet-title">{tenet.title}</h4>
                                        <p className="tenet-desc">{tenet.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;